import { useEffect, useState } from 'react'

import * as commentsApi from '@/modules/sales/api/comments.api'
import { Card } from '@/shared/components/ui/card'
import { toast } from '@/shared/lib/toast'

import * as leadsApi from '../../api/leads.api'
import { useSales } from '../../hooks/useSales'
import { useAdminDashboardStore } from '../../stores/dashboard.store'
import ReassignSalesDropdown from '../leads/ReassignSalesDropdown'

function getInitials(name = '') {
  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

const AVATAR_GRADIENTS = [
  'from-orange-400 to-orange-500',
  'from-violet-400 to-violet-500',
  'from-sky-400 to-sky-500',
  'from-emerald-400 to-emerald-500',
  'from-rose-400 to-rose-500',
  'from-amber-400 to-amber-500',
]

function avatarGradient(name = '') {
  const code = name.charCodeAt(0) || 0
  return AVATAR_GRADIENTS[code % AVATAR_GRADIENTS.length]
}

function FollowUpLeadItem({ lead, sales, onFollowUpSent, onReassigned }) {
  const [commentOpen, setCommentOpen] = useState(false)
  const [comment, setComment] = useState('')
  const [status, setStatus] = useState('idle')
  const [isSavingComment, setIsSavingComment] = useState(false)
  const [isReassigning, setIsReassigning] = useState(false)

  const leadId = String(lead._id ?? lead.id ?? '')
  const salesPersonName = lead.salesPerson?.name ?? lead.owner ?? '—'
  const currentSalesPersonId = lead.salesPerson?._id ?? lead.salesPerson ?? null
  const initials = getInitials(lead.name)
  const gradient = avatarGradient(lead.name)
  const source = lead.source ?? lead.platform ?? ''

  const isConfirmed = lead.followUpConfirmed === true
  const hasSalesPerson = Boolean(
    lead.salesPerson &&
    (lead.salesPerson._id ||
      lead.salesPerson.name ||
      typeof lead.salesPerson === 'string'),
  )

  const handleFollowUp = async () => {
    if (!leadId) {
      toast.error('Lead ID is missing')
      return
    }

    if (!hasSalesPerson) {
      toast.error('Please assign a salesperson to this lead first')
      return
    }

    if (isConfirmed) {
      toast.info('Follow-up already confirmed by salesperson')
      return
    }

    setStatus('sending')

    try {
      const response = await leadsApi.sendFollowUpEmail(leadId)
      toast.success(response?.message || 'Follow-up email sent successfully')
      setStatus('sent')
      onFollowUpSent(leadId)
      window.setTimeout(() => {
        setStatus('idle')
      }, 1500)
    } catch (error) {
      console.error('[FollowUpCard] sendFollowUpEmail error:', error)
      toast.error(error?.message || 'Failed to send follow-up email')
      setStatus('idle')
    }
  }

  const handleSaveComment = async () => {
    const message = comment.trim()
    if (!leadId) {
      toast.error('Lead ID is missing')
      return
    }
    if (!message) {
      toast.error('Please enter a comment')
      return
    }

    setIsSavingComment(true)
    try {
      await commentsApi.createComment({ leadId, message })
      toast.success('Comment saved — visible to the salesperson')
      setComment('')
      setCommentOpen(false)
    } catch (error) {
      console.error('[FollowUpCard] createComment error:', error)
      toast.error(error?.message || 'Failed to save comment')
    } finally {
      setIsSavingComment(false)
    }
  }

  const handleReassign = async (person) => {
    if (!leadId || !person?._id) return

    setIsReassigning(true)
    try {
      const result = await leadsApi.assignLead(leadId, person._id)
      toast.success(result?.message || 'Lead reassigned successfully')
      onReassigned?.(leadId, result?.data?.salesPerson || person)
    } catch (error) {
      console.error('[FollowUpCard] assignLead error:', error)
      toast.error(error?.message || 'Failed to reassign lead')
    } finally {
      setIsReassigning(false)
    }
  }

  return (
    <div className="rounded-[14px] border border-slate-100 bg-white p-4 shadow-[0_1px_3px_rgba(0,0,0,0.04)]">
      <div className="flex items-start gap-3">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br ${gradient} text-[13px] font-bold text-white`}
        >
          {initials}
        </div>

        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-900">
            <span>{lead.name}</span>

            <span className="font-normal text-slate-400">- {lead.company}</span>

            {source && (
              <>
                <span className="text-slate-300">•</span>
                <span className="text-[11px] font-medium capitalize text-slate-600">
                  {source}
                </span>
              </>
            )}
          </p>

          <p className="mt-0.5 text-[11px] text-slate-400">
            Owner:{' '}
            <span className="font-medium text-slate-600">
              {salesPersonName}
            </span>
            {lead.product ? ` · ${lead.product}` : ''}
          </p>

          <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
            {isConfirmed ? (
              <span className="inline-flex items-center rounded-full bg-emerald-100 px-3.5 py-1 text-[11px] font-semibold text-emerald-700">
                Confirmed ✓
              </span>
            ) : (
              <button
                type="button"
                disabled={status !== 'idle' || !hasSalesPerson}
                onClick={handleFollowUp}
                title={!hasSalesPerson ? 'Assign salesperson first' : undefined}
                className={`rounded-full px-3.5 py-1 text-[11px] font-semibold text-white transition ${
                  status === 'sent'
                    ? 'bg-emerald-600'
                    : 'bg-slate-900 hover:bg-slate-700'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {status === 'sending'
                  ? 'Sending...'
                  : status === 'sent'
                    ? 'Sent ✓'
                    : !hasSalesPerson
                      ? 'Assign first'
                      : lead.followUpSent
                        ? 'Follow up again'
                        : 'Follow up'}
              </button>
            )}

            <button
              type="button"
              onClick={() => setCommentOpen((prev) => !prev)}
              className={`rounded-full border px-3.5 py-1 text-[11px] font-medium transition ${
                commentOpen
                  ? 'border-slate-900 bg-white text-slate-900'
                  : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              Comment
            </button>

            <div
              className={isReassigning ? 'pointer-events-none opacity-60' : ''}
            >
              <ReassignSalesDropdown
                sales={sales}
                currentSalesPersonId={currentSalesPersonId}
                onReassign={handleReassign}
              />
            </div>
          </div>

          {commentOpen ? (
            <div className="mt-2.5 flex items-center gap-2">
              <input
                type="text"
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !isSavingComment) {
                    event.preventDefault()
                    handleSaveComment()
                  }
                }}
                placeholder="Add admin comment..."
                autoFocus
                disabled={isSavingComment}
                className="min-w-0 flex-1 rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-[11px] text-slate-700 outline-none placeholder:text-slate-400 focus:border-slate-400 focus:bg-white disabled:opacity-60"
              />
              <button
                type="button"
                onClick={handleSaveComment}
                disabled={isSavingComment || !comment.trim()}
                className="shrink-0 rounded-full bg-[#0F172A] px-4 py-1.5 text-[11px] font-semibold text-white transition hover:bg-[#0F172A] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSavingComment ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export default function FollowUpCard({ data = [] }) {
  const updateLeadInNotFollowedUp = useAdminDashboardStore(
    (state) => state.updateLeadInNotFollowedUp,
  )
  const { sales, fetchSales } = useSales()

  const list = Array.isArray(data) ? data : []

  useEffect(() => {
    fetchSales({
      page: 1,
      limit: 100,
    })
  }, [fetchSales])

  const handleFollowUpSent = (leadId) => {
    updateLeadInNotFollowedUp(leadId, {
      followUpSent: true,
      followUpSentAt: new Date().toISOString(),
    })
  }
  const handleReassigned = (leadId, salesPerson) => {
    updateLeadInNotFollowedUp(leadId, {
      salesPerson: {
        _id: salesPerson?._id ?? salesPerson?.id,
        name: salesPerson?.name,
        email: salesPerson?.email,
      },
    })
  }

  return (
    <Card className="flex h-full min-h-[280px] flex-col rounded-2xl border border-amber-200/80 bg-amber-50/60 p-4 shadow-[0_8px_24px_rgba(15,23,42,0.04)] sm:min-h-[320px] sm:rounded-[20px] sm:p-5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
          <h3 className="text-sm font-semibold text-slate-900">
            Leads not followed up
          </h3>
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-amber-400 px-1.5 text-[11px] font-bold text-white">
            {list.length}
          </span>
        </div>
        <span className="text-[11px] text-slate-400 sm:text-right">
          Comment, reassign or follow up directly
        </span>
      </div>

      <div className="mt-4 max-h-[350px] flex-1 space-y-3 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {list.length === 0 ? (
          <p className="py-6 text-center text-[12px] text-slate-400">
            All leads have been followed up!
          </p>
        ) : (
          list.map((lead, idx) => (
            <FollowUpLeadItem
              key={lead._id ?? lead.id ?? lead.name ?? idx}
              lead={lead}
              sales={sales}
              onFollowUpSent={handleFollowUpSent}
              onReassigned={handleReassigned}
            />
          ))
        )}
      </div>
    </Card>
  )
}
